import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useExpenses } from '@/contexts/expense-context';
import { InsightsService, type CategoryInsight } from '@/lib/domains/insights';
import { CategoryBreakdown } from '@/components/category-breakdown';
import { formatCurrency } from '@/lib/utils/currency';
import { ChartSkeleton, PageSkeleton } from '@/components/skeleton';

// Lazy load chart component (reduces initial bundle size)
const CategoryChart = lazy(() => import('@/components/category-chart').then(m => ({ default: m.CategoryChart })));

export default function InsightsPage() {
  const { expenses, defaultCurrency, currentUser, loading, error } = useExpenses();
  const [currentInsights, setCurrentInsights] = useState<CategoryInsight[]>([]);
  const [previousInsights, setPreviousInsights] = useState<CategoryInsight[]>([]);
  const [insightsWithComparison, setInsightsWithComparison] = useState<CategoryInsight[]>([]);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);

  // Get available months from expenses
  const availableMonths = useMemo(() =>
    InsightsService.getAvailableMonths(expenses),
    [expenses]
  );

  // Selected month/year state (defaults to current month, regardless of whether it has data)
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth());

  // Get current and previous month ranges based on selection
  const currentMonthRange = useMemo(() =>
    InsightsService.getMonthRange(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  const previousMonthRange = useMemo(() => {
    const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
    const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
    return InsightsService.getMonthRange(prevYear, prevMonth);
  }, [selectedYear, selectedMonth]);

  // Calculate totals
  const totalSpending = useMemo(() => {
    return currentInsights.reduce((sum, insight) => sum + insight.totalSpent, 0);
  }, [currentInsights]);

  const totalTransactions = useMemo(() => {
    return currentInsights.reduce((sum, insight) => sum + insight.transactionCount, 0);
  }, [currentInsights]);

  const previousTotalSpending = useMemo(() => {
    return previousInsights.reduce((sum, insight) => sum + insight.totalSpent, 0);
  }, [previousInsights]);

  const spendingChange = useMemo(() => {
    if (previousTotalSpending === 0) return null;
    const change = ((totalSpending - previousTotalSpending) / previousTotalSpending) * 100;
    return change;
  }, [totalSpending, previousTotalSpending]);

  // Load insights
  useEffect(() => {
    async function loadInsights() {
      if (!currentUser || expenses.length === 0) {
        setIsLoadingInsights(false);
        return;
      }

      setIsLoadingInsights(true);

      try {
        // Get current month insights
        const current = await InsightsService.getCategoryBreakdown(
          expenses,
          currentMonthRange.start,
          currentMonthRange.end,
          defaultCurrency,
          currentUser.id,
          'light'
        );
        setCurrentInsights(current);

        // Get previous month insights for comparison
        const previous = await InsightsService.getCategoryBreakdown(
          expenses,
          previousMonthRange.start,
          previousMonthRange.end,
          defaultCurrency,
          currentUser.id,
          'light'
        );
        setPreviousInsights(previous);

        // Compare periods
        const withComparison = InsightsService.comparePeriods(current, previous);
        setInsightsWithComparison(withComparison);
      } catch (err) {
        console.error('Failed to load insights:', err);
      } finally {
        setIsLoadingInsights(false);
      }
    }

    loadInsights();
  }, [expenses, currentUser, defaultCurrency, currentMonthRange, previousMonthRange, selectedYear, selectedMonth]);

  // Get top category
  const topCategory = useMemo(() => {
    if (currentInsights.length === 0) return null;
    return currentInsights[0];
  }, [currentInsights]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Insights</h1>

          {/* Month/Year Selector */}
          {availableMonths.length > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const currentIndex = availableMonths.findIndex(
                      m => m.year === selectedYear && m.month === selectedMonth
                    );
                    if (currentIndex < availableMonths.length - 1) {
                      const prev = availableMonths[currentIndex + 1];
                      setSelectedYear(prev.year);
                      setSelectedMonth(prev.month);
                    }
                  }}
                  disabled={
                    availableMonths.findIndex(
                      m => m.year === selectedYear && m.month === selectedMonth
                    ) === availableMonths.length - 1
                  }
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous month"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
                  {InsightsService.formatMonthYear(currentMonthRange.start)}
                </span>
                <button
                  onClick={() => {
                    const currentIndex = availableMonths.findIndex(
                      m => m.year === selectedYear && m.month === selectedMonth
                    );
                    if (currentIndex > 0) {
                      const next = availableMonths[currentIndex - 1];
                      setSelectedYear(next.year);
                      setSelectedMonth(next.month);
                    }
                  }}
                  disabled={
                    availableMonths.findIndex(
                      m => m.year === selectedYear && m.month === selectedMonth
                    ) === 0
                  }
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next month"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              {(selectedYear !== new Date().getFullYear() || selectedMonth !== new Date().getMonth()) && (
                <button
                  onClick={() => {
                    setSelectedYear(new Date().getFullYear());
                    setSelectedMonth(new Date().getMonth());
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  Current Month
                </button>
              )}
            </div>
          )}

          {/* Static display when only one month */}
          {availableMonths.length <= 1 && (
            <p className="text-gray-600 mt-1">
              {InsightsService.formatMonthYear(currentMonthRange.start)}
            </p>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading || isLoadingInsights ? (
          <PageSkeleton />
        ) : (
          <>
            {/* Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                    Total Spending
                  </h2>
                  <p className="text-3xl font-bold text-teal-600">
                    {formatCurrency(totalSpending, defaultCurrency)}
                  </p>
                  {spendingChange !== null && (
                    <p className={`text-sm mt-1 ${spendingChange > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                      {spendingChange > 0 ? '+' : ''}{spendingChange.toFixed(1)}% vs last month
                    </p>
                  )}
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                    Transactions
                  </h2>
                  <p className="text-3xl font-bold text-gray-900">
                    {totalTransactions}
                  </p>
                  {totalTransactions > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Avg {formatCurrency(totalSpending / totalTransactions, defaultCurrency)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Spending Distribution Chart */}
            {currentInsights.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Top Categories
                </h2>
                <Suspense fallback={<ChartSkeleton />}>
                  <CategoryChart
                    insights={currentInsights}
                    currency={defaultCurrency}
                  />
                </Suspense>
                {currentInsights.length > 8 && (
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    Showing top 8 categories • See full breakdown below
                  </p>
                )}
              </div>
            )}

            {/* Insights */}
            {topCategory && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-900 flex items-start gap-2">
                  <span className="text-xl">💡</span>
                  <span>
                    <strong>{topCategory.category.displayName}</strong> is your biggest
                    spending category this month at{' '}
                    <strong>{formatCurrency(topCategory.totalSpent, defaultCurrency)}</strong>
                    {' '}({topCategory.percentage.toFixed(0)}% of total spending)
                  </span>
                </p>
              </div>
            )}

            {/* Category Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Category Breakdown
              </h2>
              <CategoryBreakdown
                insights={insightsWithComparison}
                currency={defaultCurrency}
                showComparison={previousInsights.length > 0}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
